/**
 * Alles wat met de API praat, op één plek. De schermen gebruiken dezelfde endpoints als een
 * externe partij straks, zoals in ADTS-ONE.
 */
const BASIS = '/api/v1';

export class ApiFout extends Error {
  constructor(status, lichaam) {
    super(lichaam?.error?.message ?? `Fout ${status}`);
    this.status = status;
    this.code = lichaam?.error?.code;
    this.details = lichaam?.error?.details;
  }
}

async function vraag(pad, opties = {}) {
  /* Nooit uit de cache van de browser: tijdens een herstart gaf nginx eens de wachtpagina met 200, en die bleef
     uren in de cache staan als antwoord op de API (2026-10-21). */
  const res = await fetch(`${BASIS}${pad}`, {
    cache: 'no-store',
    credentials: 'same-origin',
    headers: opties.body ? { 'content-type': 'application/json' } : undefined,
    ...opties,
  });
  if (res.status === 204) return null;
  const lichaam = await res.json().catch(() => null);
  if (!res.ok) throw new ApiFout(res.status, lichaam);
  /* Een antwoord zonder JSON (de wachtpagina van nginx bij een herstart) is een fout, geen lege pagina. Het
     scherm vertaalt de melding: t('ADTS ZERO wordt bijgewerkt. Probeer het zo opnieuw.') */
  if (lichaam === null) throw new ApiFout(503, { error: { message: 'ADTS ZERO wordt bijgewerkt. Probeer het zo opnieuw.' } });
  return lichaam;
}

const json = (methode, body) => ({ method: methode, body: JSON.stringify(body) });

/** De kaart: periode, klant en filters als één querystring; lijsten met komma's. */
function kaartVraag(tenant, range, filter) {
  const q = new URLSearchParams({ range });
  if (tenant && tenant !== 'alle') q.set('tenant', tenant);
  for (const [k, v] of Object.entries(filter)) {
    const w = Array.isArray(v) ? v.join(',') : v;
    if (w !== undefined && w !== null && w !== '') q.set(k, String(w));
  }
  return q;
}

export const api = {
  aanmeldwegen: () => vraag('/auth/methods'),
  aanmelden: (number, password, code) => vraag('/auth/sign-in', json('POST', { number, password, code })),
  afmelden: () => vraag('/auth/sign-out', { method: 'POST' }),
  wieBenIk: () => vraag('/auth/me'),
  zetInstellingen: (invoer) => vraag('/auth/settings', json('PATCH', invoer)),
  wijzigWachtwoord: (current, next) => vraag('/auth/change-password', json('POST', { current, next })),

  logboek: ({ tenant, before } = {}) => {
    const q = new URLSearchParams();
    if (tenant && tenant !== 'alle') q.set('tenant', tenant);
    if (before) q.set('before', before);
    return vraag(`/log?${q}`);
  },

  bronnen: (tenant) => vraag(`/sources${tenant && tenant !== 'alle' ? `?tenant=${encodeURIComponent(tenant)}` : ''}`),
  wisOnbekend: (tenant, senderIp, host) => vraag('/sources/unknown/ignore', json('POST', { tenant: Number(tenant), senderIp, host: host ?? null })),
  bron: (id) => vraag(`/sources/${encodeURIComponent(id)}`),
  versiesOphalen: () => vraag('/sources/versions/refresh', { method: 'POST' }),
  maakBron: (invoer) => vraag('/sources', json('POST', invoer)),
  /* De API van een FMC: eerst het certificaat, dan testen, dan opslaan (docs/besluiten/api-gegevens-van-een-bron.md). */
  apiCertificaat: (url, purpose, sensorId) => vraag('/sources/api/certificate', json('POST', { url, ...(purpose ? { purpose } : {}), ...(sensorId ? { sensorId } : {}) })),
  apiTest: (purpose, g, sensorId) => vraag('/sources/api/test', json('POST', { purpose, ...g, ...(sensorId ? { sensorId } : {}) })),
  zetBronApi: (id, purpose, g) => vraag(`/sources/${encodeURIComponent(id)}/api/${purpose}`, json('PUT', g)),
  verwijderBronApi: (id, purpose) => vraag(`/sources/${encodeURIComponent(id)}/api/${purpose}`, { method: 'DELETE' }),
  /* De syslog-standaard (src/modules/uitrol/): vergelijken leest live van de FMC. */
  syslogStandaard: (id) => vraag(`/sources/${encodeURIComponent(id)}/syslog`),
  zetSyslogRoute: (id, route) => vraag(`/sources/${encodeURIComponent(id)}/syslog/route`, json('PUT', { route })),
  rolSyslogUit: (id, hash, ookAndere, onderbrekingMag) => vraag(`/sources/${encodeURIComponent(id)}/syslog/rollouts`, json('POST', { hash, ookAndere, onderbrekingMag })),
  draaiSyslogTerug: (id, rid) => vraag(`/sources/${encodeURIComponent(id)}/syslog/rollouts/${encodeURIComponent(rid)}/undo`, { method: 'POST' }),
  toegangsregels: (id) => vraag(`/sources/${encodeURIComponent(id)}/rules`),
  fmcBeleid: (id) => vraag(`/sources/${encodeURIComponent(id)}/policies`),
  maakFmcBeleid: (id, invoer) => vraag(`/sources/${encodeURIComponent(id)}/policies`, json('POST', invoer)),
  hernoemFmcBeleid: (id, soort, pid, naam) => vraag(`/sources/${encodeURIComponent(id)}/policies/${soort}/${encodeURIComponent(pid)}`, json('PUT', { naam })),
  wijsBeleidToe: (id, invoer) => vraag(`/sources/${encodeURIComponent(id)}/syslog/assign`, json('POST', invoer)),
  wijzigBron: (id, invoer) => vraag(`/sources/${encodeURIComponent(id)}`, json('PATCH', invoer)),
  verwijderBron: (id, invoer) => vraag(`/sources/${encodeURIComponent(id)}/remove`, json('POST', invoer)),
  zetBronTerug: (id) => vraag(`/sources/${encodeURIComponent(id)}/restore`, { method: 'POST' }),

  alerts: (tenant, status, zoek, { dagen, door } = {}) => {
    const q = new URLSearchParams();
    if (tenant && tenant !== 'alle') q.set('tenant', tenant);
    if (status) q.set('status', status);
    if (zoek) q.set('q', zoek);
    if (dagen) q.set('days', dagen);
    if (door) q.set('closedBy', door);
    return vraag(`/alerts?${q}`);
  },
  alert: (nummer) => vraag(`/alerts/${encodeURIComponent(nummer)}`),
  controleerAlert: (nummer) => vraag(`/alerts/${encodeURIComponent(nummer)}/recheck`, { method: 'POST' }),
  pakAlertOp: (nummer) => vraag(`/alerts/${encodeURIComponent(nummer)}/pick-up`, { method: 'POST' }),
  alertNotitie: (nummer, text) => vraag(`/alerts/${encodeURIComponent(nummer)}/notes`, json('POST', { text })),
  sluitAlert: (nummer, reason) => vraag(`/alerts/${encodeURIComponent(nummer)}/close`, json('POST', { reason })),

  /* Nessus (docs/besluiten/nessus-koppeling.md). */
  scans: (tenant) => vraag(`/nessus${tenant && tenant !== 'alle' ? `?tenant=${encodeURIComponent(tenant)}` : ''}`),
  scanAdres: (id) => vraag(`/nessus/hosts/${encodeURIComponent(id)}`),
  nessusKoppeling: () => vraag('/nessus/link'),
  nessusCertificaat: (url) => vraag('/nessus/link/certificate', json('POST', { url })),
  zetNessusKoppeling: (g) => vraag('/nessus/link', json('PUT', g)),
  verwijderNessusKoppeling: () => vraag('/nessus/link', { method: 'DELETE' }),
  nessusNu: () => vraag('/nessus/sync', { method: 'POST' }),
  klantLocaties: (code) => vraag(`/admin/tenants/${encodeURIComponent(code)}/locations`),
  maakLocatie: (code, invoer) => vraag(`/admin/tenants/${encodeURIComponent(code)}/locations`, json('POST', invoer)),
  wijzigLocatie: (code, id, invoer) => vraag(`/admin/tenants/${encodeURIComponent(code)}/locations/${encodeURIComponent(id)}`, json('PATCH', invoer)),
  verwijderLocatie: (code, id) => vraag(`/admin/tenants/${encodeURIComponent(code)}/locations/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  ipReeksen: (code) => vraag(`/admin/tenants/${encodeURIComponent(code)}/ranges`),
  maakIpReeks: (code, invoer) => vraag(`/admin/tenants/${encodeURIComponent(code)}/ranges`, json('POST', invoer)),
  verwijderIpReeks: (code, id) => vraag(`/admin/tenants/${encodeURIComponent(code)}/ranges/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  anyconnect: (tenant) => vraag(`/vpn/anyconnect${tenant && tenant !== 'alle' ? `?tenant=${tenant}` : ''}`),
  identity: (tenant, q) => vraag(`/identity?${new URLSearchParams({ ...(q ? { q } : {}), ...(tenant && tenant !== 'alle' ? { tenant } : {}) })}`),
  vpn: (tenant, range) => vraag(`/vpn?${new URLSearchParams({ range, ...(tenant && tenant !== 'alle' ? { tenant } : {}) })}`),
  kaart: (tenant, range, filter = {}) => vraag(`/map?${kaartVraag(tenant, range, filter)}`),
  kaartLive: (tenant, range, filter = {}) => `/api/v1/map/stream?${kaartVraag(tenant, range, filter)}`,
  kaartIp: (ip, tenant, range) => {
    const q = new URLSearchParams({ range });
    if (tenant && tenant !== 'alle') q.set('tenant', tenant);
    return vraag(`/map/ip/${encodeURIComponent(ip)}?${q}`);
  },

  beheerMeldingen: () => vraag('/admin/notifications'),
  zetBeheerMeldingen: (invoer) => vraag('/admin/notifications', json('PATCH', invoer)),
  /* Meldingsregels per klant (volledig per klant). */
  notificatiesKlant: (code) => vraag(`/admin/notifications/tenant/${encodeURIComponent(code)}`),
  zetNotificatiesKlant: (code, invoer) => vraag(`/admin/notifications/tenant/${encodeURIComponent(code)}`, json('PUT', invoer)),
  beheerDetectie: () => vraag('/admin/detection'),
  zetBeheerDetectie: (invoer) => vraag('/admin/detection', json('PATCH', invoer)),
  modulesOverzicht: () => vraag('/modules'),
  tunnels: (tenant, dagen) => vraag(`/tunnels?${new URLSearchParams({ ...(tenant && tenant !== 'alle' ? { tenant } : {}), ...(dagen ? { days: dagen } : {}) })}`),
  klantModules: (code) => vraag(`/admin/tenants/${encodeURIComponent(code)}/modules`),
  zetKlantModule: (code, module, invoer) => vraag(`/admin/tenants/${encodeURIComponent(code)}/modules/${module}`, json('PUT', invoer)),
  mijnModules: () => vraag('/modules'),
  zetMijnModule: (module, invoer) => vraag(`/modules/${module}`, json('PUT', invoer)),
  interesseModule: (module) => vraag(`/modules/${module}/interest`, { method: 'POST' }),
  meekijken: (code) => vraag(`/meekijken/${encodeURIComponent(code)}`),
  meekijkAlerts: (code, status) => vraag(`/meekijken/${encodeURIComponent(code)}/alerts${status ? `?status=${status}` : ''}`),
  meekijkAlert: (code, nummer) => vraag(`/meekijken/${encodeURIComponent(code)}/alerts/${encodeURIComponent(nummer)}`),
  blokkades: (tenant) => vraag(`/blocks${tenant && tenant !== 'alle' ? `?tenant=${tenant}` : ''}`),
  blokkadesWachten: (tenant) => vraag(`/blocks/waiting${tenant && tenant !== 'alle' ? `?tenant=${tenant}` : ''}`),
  blokkade: (nr) => vraag(`/blocks/${encodeURIComponent(nr)}`),
  blokkadePlan: (nr) => vraag(`/blocks/${encodeURIComponent(nr)}/plan`),
  stelBlokkadeVoor: (invoer) => vraag('/blocks', json('POST', invoer)),
  keurBlokkadeGoed: (nr, ttlHours) => vraag(`/blocks/${encodeURIComponent(nr)}/approve`, json('POST', { ttlHours })),
  wijsBlokkadeAf: (nr, reason) => vraag(`/blocks/${encodeURIComponent(nr)}/reject`, json('POST', { reason })),
  hefBlokkadeOp: (nr) => vraag(`/blocks/${encodeURIComponent(nr)}/lift`, json('POST', {})),
  blokInrichting: (id) => vraag(`/blocks/setup/${id}`),
  maakBlokObject: (id) => vraag(`/blocks/setup/${id}/object`, json('POST', {})),
  intel: () => vraag('/intel'),
  intelVerversen: () => vraag('/intel/refresh', { method: 'POST' }),
  /* Firewall: één doelpoort, of de IPS-meldingen (soort en regel naar keuze). */
  mailDreigingen: (tenant, dagen, actie) => vraag(`/charts/mail/threats?${new URLSearchParams({ days: dagen, action: actie,
    ...(tenant && tenant !== 'alle' ? { tenant } : {}) })}`),
  firewallDetail: (wat, tenant, dagen, velden) => vraag(`/charts/firewall/${wat}?${new URLSearchParams({ days: dagen,
    ...(tenant && tenant !== 'alle' ? { tenant } : {}), ...Object.fromEntries(Object.entries(velden).filter(([, v]) => v !== null && v !== undefined)) })}`),
  grafiek: (soort, tenant, dagen) => vraag(`/charts/${soort}?${dagen ? `days=${dagen}` : ''}${tenant && tenant !== 'alle' ? `&tenant=${tenant}` : ''}`),
  umbrellaGrafieken: (tenant, dagen) => vraag(`/umbrella/charts?days=${dagen}${tenant && tenant !== 'alle' ? `&tenant=${tenant}` : ''}`),
  meekijkUmbrella: (code, dagen) => vraag(`/meekijken/${encodeURIComponent(code)}/umbrella?days=${dagen}`),
  endpoints: (tenant, dagen) => vraag(`/secure-endpoint/endpoints?days=${dagen}${tenant && tenant !== 'alle' ? `&tenant=${tenant}` : ''}`),
  meekijkEndpoints: (code, dagen) => vraag(`/meekijken/${encodeURIComponent(code)}/endpoints?days=${dagen}`),
  /* De beveiligingsstatus (docs/ontwerp/beveiligingsstatus.md): medewerkers per klant, de klant zelf, meekijken, beheer. */
  status: (tenant) => vraag(`/status?tenant=${encodeURIComponent(tenant)}`),
  statusKlanten: () => vraag('/status/tenants'),
  statusScore: (tenant) => vraag(`/status/score${tenant && tenant !== 'alle' ? `?tenant=${encodeURIComponent(tenant)}` : ''}`),
  mijnStatus: () => vraag('/status'),
  mijnScore: () => vraag('/status/score'),
  meekijkStatus: (code) => vraag(`/meekijken/${encodeURIComponent(code)}/status`),
  klantDomeinen: (code) => vraag(`/admin/tenants/${encodeURIComponent(code)}/domains`),
  voegDomeinToe: (code, domain) => vraag(`/admin/tenants/${encodeURIComponent(code)}/domains`, json('POST', { domain })),
  controleerDomein: (code, domain) => vraag(`/admin/tenants/${encodeURIComponent(code)}/domains/${encodeURIComponent(domain)}/check`, { method: 'POST' }),
  verwijderDomein: (code, domain) => vraag(`/admin/tenants/${encodeURIComponent(code)}/domains/${encodeURIComponent(domain)}`, { method: 'DELETE' }),
  zetOmNaarKlant: (code) => vraag(`/admin/tenants/${encodeURIComponent(code)}/convert`, { method: 'POST' }),
  zetKlantActief: (code, active) => vraag(`/admin/tenants/${encodeURIComponent(code)}/active`, json('PUT', { active })),
  wisKlant: (code, confirm) => vraag(`/admin/tenants/${encodeURIComponent(code)}/erase`, json('POST', { confirm })),
  klantDiensten: (code) => vraag(`/admin/tenants/${encodeURIComponent(code)}/services`),
  zetKlantDiensten: (code, invoer) => vraag(`/admin/tenants/${encodeURIComponent(code)}/services`, json('PUT', invoer)),
  seOrganisaties: () => vraag('/secure-endpoint/orgs'),
  maakSeOrganisatie: (invoer) => vraag('/secure-endpoint/orgs', json('POST', invoer)),
  verwijderSeOrganisatie: (id) => vraag(`/secure-endpoint/orgs/${id}`, { method: 'DELETE' }),
  seNu: () => vraag('/secure-endpoint/sync', { method: 'POST' }),
  umbrellaKoppeling: () => vraag('/umbrella/link'),
  zetUmbrellaKoppeling: (invoer) => vraag('/umbrella/link', json('PUT', invoer)),
  verwijderUmbrellaKoppeling: () => vraag('/umbrella/link', { method: 'DELETE' }),
  umbrellaKlantenVernieuwen: () => vraag('/umbrella/customers/refresh', { method: 'POST' }),
  koppelUmbrellaKlant: (org, tenant) => vraag(`/umbrella/orgs/${org}`, json('PUT', { tenant })),
  umbrellaNu: () => vraag('/umbrella/sync', { method: 'POST' }),
  /* Cisco Duo (docs/ontwerp/duo.md): Beheer › Duo en de pagina Duo. */
  duoKoppeling: () => vraag('/duo/link'),
  connectiviteit: (invoer) => vraag('/admin/connectivity', json('POST', invoer)),
  onthul: (token) => vraag('/reveal', json('POST', { token })),
  zetDuoKoppeling: (invoer) => vraag('/duo/link', json('PUT', invoer)),
  verwijderDuoKoppeling: () => vraag('/duo/link', { method: 'DELETE' }),
  duoHoofdaccountHuidig: () => vraag('/duo/parent/current', { method: 'POST' }),
  zetDuoHoofdaccount: (invoer) => vraag('/duo/parent', json('PUT', invoer)),
  verwijderDuoHoofdaccount: () => vraag('/duo/parent', { method: 'DELETE' }),
  duoAccountsVernieuwen: () => vraag('/duo/accounts/refresh', { method: 'POST' }),
  koppelDuoAccount: (account, tenant) => vraag(`/duo/accounts/${encodeURIComponent(account)}`, json('PUT', { tenant })),
  duoNu: () => vraag('/duo/sync', { method: 'POST' }),
  duo: (tenant, q) => vraag(`/duo?${new URLSearchParams({ ...(q ? { q } : {}), ...(tenant && tenant !== 'alle' ? { tenant } : {}) })}`),
  verwachtePoorten: () => vraag('/nessus/expected-ports'),
  maakVerwachtePoort: (invoer) => vraag('/nessus/expected-ports', json('POST', invoer)),
  verwijderVerwachtePoort: (id) => vraag(`/nessus/expected-ports/${id}`, { method: 'DELETE' }),
  zetKanaal: (kanaal, invoer) => vraag(`/admin/notifications/channels/${kanaal}`, json('PUT', invoer)),
  controleerKanaal: (kanaal, invoer) => vraag(`/admin/notifications/channels/${kanaal}/check`, json('POST', invoer)),
  testMeldingAlgemeen: () => vraag('/admin/notifications/test', { method: 'POST' }),
  testMelding: (code) => vraag(`/admin/notifications/tenant/${encodeURIComponent(code)}/test`, { method: 'POST' }),

  schermen: () => vraag('/admin/screens'),
  maakScherm: (invoer) => vraag('/admin/screens', json('POST', invoer)),
  trekSchermIn: (id) => vraag(`/admin/screens/${encodeURIComponent(id)}/revoke`, { method: 'POST' }),
  sensoren: () => vraag('/admin/sensors'),
  maakSensor: (invoer) => vraag('/admin/sensors', json('POST', invoer)),
  hernoemSensor: (id, name) => vraag(`/admin/sensors/${encodeURIComponent(id)}`, json('PATCH', { name })),
  sensorCode: (id) => vraag(`/admin/sensors/${encodeURIComponent(id)}/code`, { method: 'POST' }),
  trekSensorIn: (id) => vraag(`/admin/sensors/${encodeURIComponent(id)}/revoke`, { method: 'POST' }),

  beheer: () => vraag('/admin'),
  maakKlant: (invoer) => vraag('/admin/tenants', json('POST', invoer)),
  wijzigKlant: (code, invoer) => vraag(`/admin/tenants/${encodeURIComponent(code)}`, json('PATCH', invoer)),
  wijzigMedewerker: (nummer, invoer) => vraag(`/admin/employees/${encodeURIComponent(nummer)}`, json('PATCH', invoer)),
  wijzigKlantgebruiker: (id, invoer) => vraag(`/admin/customer-users/${encodeURIComponent(id)}`, json('PATCH', invoer)),
  maakRol: (invoer) => vraag('/admin/roles', json('POST', invoer)),
  wijzigRol: (code, invoer) => vraag(`/admin/roles/${encodeURIComponent(code)}`, json('PATCH', invoer)),
  verwijderRol: (code) => vraag(`/admin/roles/${encodeURIComponent(code)}`, { method: 'DELETE' }),
};
